// Guest orders (slice 66, ADR 0066): what guests asked for from their tables and how
// each request ended. Read-only — the cashier's till is where an order is accepted or
// turned down; this page is where an owner sees whether QR ordering is working.
//
// Nothing here can change an order, and no price is shown because none is stored: the
// till prices what it accepts from its own menu.

import type { JSX } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { apiFetch } from '../api/client.js';
import type { GuestBoardFilter, GuestBoardResponse } from '../api/types.js';
import { useAuth } from '../auth/AuthContext.js';
import { Chip, QueryState } from './pos/common.js';
import {
  FILTERS,
  WINDOWS,
  ago,
  orderSummary,
  statusLabel,
  statusTone,
  stuckHint,
} from './guest-orders/format.js';

const REFRESH_MS = 10_000;

function parseFilter(raw: string | null): GuestBoardFilter {
  return FILTERS.some((f) => f.value === raw) ? (raw as GuestBoardFilter) : 'all';
}
function parseHours(raw: string | null): number {
  const n = Number(raw);
  return WINDOWS.some((w) => w.hours === n) ? n : 24;
}

export function GuestOrdersPage(): JSX.Element {
  const { orgId, outletId } = useParams<{ orgId: string; outletId: string }>();
  const { user } = useAuth();
  const [params, setParams] = useSearchParams();
  const filter = parseFilter(params.get('filter'));
  const hours = parseHours(params.get('hours'));

  const board = useQuery({
    queryKey: ['guest-orders', orgId, outletId, filter, hours],
    queryFn: () =>
      apiFetch<GuestBoardResponse>(
        `/organizations/${orgId}/outlets/${outletId}/guest-orders?filter=${filter}&hours=${hours}&limit=100`,
      ),
    enabled: !!orgId && !!outletId,
    // Changing the filter keeps the old list on screen until the new one arrives.
    placeholderData: keepPreviousData,
    refetchInterval: REFRESH_MS,
    refetchIntervalInBackground: false,
  });

  function choose(next: { filter?: GuestBoardFilter; hours?: number }): void {
    const q = new URLSearchParams(params);
    if (next.filter) q.set('filter', next.filter);
    if (next.hours) q.set('hours', String(next.hours));
    setParams(q, { replace: true });
  }

  const data = board.data;
  const hint = data ? stuckHint(data.counts) : null;
  const now = Date.now();

  return (
    <main className="page">
      <header className="page-header">
        <div>
          <h1>Guest orders</h1>
          <p className="muted">
            Signed in as <strong>{user?.email}</strong> ·{' '}
            <Link to={`/orgs/${orgId}/outlets/${outletId}/tables`}>Tables &amp; QR</Link>
          </p>
        </div>
      </header>

      <section className="card" aria-labelledby="orders-how">
        <h2 id="orders-how">How it works</h2>
        <p>
          Guests send an order from the QR on their table. It reaches the kitchen only when your
          cashier accepts it on the till. This page only shows what happened; it refreshes by
          itself.
        </p>
      </section>

      <QueryState loading={board.isLoading} error={board.error} />

      {data ? (
        <>
          <section className="tiles" aria-label="Orders in this period">
            {(['waiting', 'accepted', 'rejected', 'expired'] as const).map((s) => (
              <div key={s} className="tile">
                <div className="tile-label">{statusLabel(s)}</div>
                <div className="tile-value">{data.counts[s]}</div>
              </div>
            ))}
          </section>

          {hint ? (
            <p className="error" role="alert">
              {hint}
            </p>
          ) : null}

          <div className="chip-row" role="group" aria-label="Show">
            {FILTERS.map((f) => (
              <button
                key={f.value}
                type="button"
                className={f.value === filter ? 'chip chip-active' : 'chip'}
                aria-pressed={f.value === filter}
                onClick={() => choose({ filter: f.value })}
              >
                {f.label}
              </button>
            ))}
          </div>
          <div className="chip-row" role="group" aria-label="Period">
            {WINDOWS.map((w) => (
              <button
                key={w.hours}
                type="button"
                className={w.hours === hours ? 'chip chip-active' : 'chip'}
                aria-pressed={w.hours === hours}
                onClick={() => choose({ hours: w.hours })}
              >
                {w.label}
              </button>
            ))}
          </div>

          {data.orders.length === 0 ? (
            <p className="muted">
              {filter === 'all'
                ? 'No guest orders in this period.'
                : 'No orders match this filter in this period.'}
            </p>
          ) : (
            <ul className="feedback-list">
              {data.orders.map((o) => (
                <li key={o.id} className="card">
                  <div className="table-card-head">
                    <h3>Table {o.tableLabel}</h3>
                    <Chip tone={statusTone(o.status)}>{statusLabel(o.status)}</Chip>
                  </div>
                  <ul>
                    {o.lines.map((l, i) => (
                      <li key={i}>
                        {l.quantity} × {l.name}
                      </li>
                    ))}
                  </ul>
                  <p className="muted">
                    {orderSummary(o)} · {ago(o.createdAt, now)}
                  </p>
                </li>
              ))}
            </ul>
          )}
          {data.orders.length >= 100 ? <p className="muted">Showing the 100 most recent.</p> : null}
        </>
      ) : null}
    </main>
  );
}
